import { FC, useState } from "react";
import { clsx } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { GetClaimsQuery } from "graphql/queries/__generated__/claims";
import ClaimDocumentViewerContainer from "./ClaimDocumentViewer";
import ClaimThreadContainer from "./ClaimThread";

type ClaimData = NonNullable<NonNullable<GetClaimsQuery["getClaims"]>[number]>;

interface Props {
    claim: ClaimData;
}

const ClaimActionButtons: FC<Props> = ({ claim }) => {
    const { t } = useTranslation();
    const [showDocumentViewer, setShowDocumentViewer] = useState(false);
    const [showThread, setShowThread] = useState(false);

    const viewDocuments = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDocumentViewer(true);
    };

    const openThread = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowThread(true);
    };

    // Styling constants
    const baseButtonClass =
        "inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 ease-in-out transform hover:scale-105";

    const buttonClasses = {
        docs: clsx(
            baseButtonClass,
            "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-gray-400",
        ),
        thread: clsx(
            baseButtonClass,
            "text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:ring-indigo-500",
        ),
    };

    const iconClass = "w-5 h-5 mr-2";

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-center items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <button
                    onClick={viewDocuments}
                    className={buttonClasses.docs}
                    title={t("actions.view_documents")}>
                    <svg
                        className={iconClass}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    {t("common.buttons.docs")}
                </button>

                <button
                    onClick={openThread}
                    className={buttonClasses.thread}
                    title={t("actions.open_thread")}>
                    <svg
                        className={iconClass}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                    </svg>
                    {t("common.buttons.thread")}
                </button>
            </div>

            <ClaimDocumentViewerContainer
                open={showDocumentViewer}
                setOpen={setShowDocumentViewer}
                claimId={claim.offer_claim_id || ""}
            />

            <ClaimThreadContainer
                open={showThread}
                setOpen={setShowThread}
                claim={claim}
            />
        </div>
    );
};

export default ClaimActionButtons;
